import mixpanel from 'mixpanel-browser';

/**
 * Small, never-throwing wrapper around mixpanel-browser for the web app.
 *
 * - Every call is guarded: a missing key, a failed init, or a blocked/broken
 *   Mixpanel (ad blockers, privacy browsers) must never break the app.
 * - `platform: 'web'` is registered as a super property right after init and
 *   again after every reset, because mixpanel.reset() clears super properties.
 */
export const WEB_SUPER_PROPERTIES = Object.freeze({ platform: 'web' });

let initialized = false;

const safely = (fn) => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

export const registerWebSuperProperties = () => {
  if (!initialized) return false;
  return safely(() => {
    mixpanel.register({ ...WEB_SUPER_PROPERTIES });
    return true;
  }) === true;
};

export const initAnalytics = (key, config = { ignore_dnt: true }) => {
  if (!key) return false;
  const ok = safely(() => {
    mixpanel.init(key, config);
    return true;
  }) === true;
  initialized = ok;
  if (ok) registerWebSuperProperties();
  return ok;
};

/** Clear identity on logout, then re-register the web super properties. */
export const resetAnalytics = () => {
  if (!initialized) return false;
  safely(() => mixpanel.reset());
  return registerWebSuperProperties();
};

/**
 * Formation save. Same event name and property shape as mobile
 * (squigglygames/mobile hooks/useAnalytics.tsx trackSetTeam:
 * { team_id?: string; formation?: number; was_first_save?: boolean },
 * called as trackSetTeam({ formation }) on a 200 in hooks/useSaveStrategy.ts).
 */
export const trackSetTeam = ({ formation } = {}) => {
  if (!initialized) return false;
  const props = Number.isInteger(formation) ? { formation } : {};
  return safely(() => {
    mixpanel.track('set_team', props);
    return true;
  }) === true;
};

/** Test-only: reset module state between tests. */
export const __resetAnalyticsStateForTests = () => {
  initialized = false;
};
